/* eslint-disable no-unused-vars */
import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  User,
  Phone,
  MapPin,
  Calendar,
  FileText,
  CheckCircle,
  AlertCircle,
  Save,
  Clock,
  CreditCard,
} from "lucide-react";
import { AdminContext } from "../../../context/AdminContext";

const NewInvoice = () => {
  const navigate = useNavigate();
  const { addManualPayment } = useContext(AdminContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const initialFormState = {
    payerName: "",
    payerPhone: "",
    payerAddress: "",
    type: "client",
    amountToPay: "",
    description: "",

    // Due date options
    dueOption: "1_week", // default due in 1 week
    customDueDate: "",

    // Pay now option
    payNow: false,
    initialPaidAmount: "",
    initialNote: "",
  };

  // Form State
  const [formData, setFormData] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Due date options
  const dueDateOptions = [
    { value: "1_week", label: "1 Week" },
    { value: "2_weeks", label: "2 Weeks" },
    { value: "3_weeks", label: "3 Weeks" },
    { value: "1_month", label: "1 Month" },
    { value: "custom", label: "Custom Date" },
  ];

  // Calculate due date based on selection
  const calculateDueDate = () => {
    if (formData.dueOption === "custom" && formData.customDueDate) {
      return formData.customDueDate;
    }

    const today = new Date();
    switch (formData.dueOption) {
      case "1_week":
        return new Date(today.setDate(today.getDate() + 7))
          .toISOString()
          .split("T")[0];
      case "2_weeks":
        return new Date(today.setDate(today.getDate() + 14))
          .toISOString()
          .split("T")[0];
      case "3_weeks":
        return new Date(today.setDate(today.getDate() + 21))
          .toISOString()
          .split("T")[0];
      case "1_month":
        return new Date(today.setMonth(today.getMonth() + 1))
          .toISOString()
          .split("T")[0];
      default:
        return new Date(today.setDate(today.getDate() + 7))
          .toISOString()
          .split("T")[0];
    }
  };

  // Validation Logic
  const validateField = (name, value) => {
    let error = "";
    switch (name) {
      case "payerName":
        if (!value.trim()) error = "Name is required";
        break;
      case "payerPhone":
        if (!value) error = "Phone is required";
        else if (!/^\d{10}$/.test(value)) error = "Must be 10 digits";
        break;
      case "payerAddress":
        if (!value.trim()) error = "Address is required";
        break;
      case "amountToPay":
        if (!value) error = "Amount is required";
        else if (isNaN(Number(value)) || Number(value) <= 0)
          error = "Invalid amount";
        break;
      case "initialPaidAmount":
        if (
          formData.payNow &&
          (!value || isNaN(Number(value)) || Number(value) <= 0)
        )
          error = "Invalid payment amount";
        else if (
          formData.payNow &&
          Number(value) > Number(formData.amountToPay || 0)
        )
          error = "Cannot exceed total amount";
        break;
      case "customDueDate":
        if (formData.dueOption === "custom" && !value)
          error = "Custom due date is required";
        else if (formData.dueOption === "custom" && value) {
          const selectedDate = new Date(value);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          if (selectedDate < today) error = "Due date cannot be in the past";
        }
        break;
    }
    return error;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
      // Reset custom due date if not selected
      ...(name === "dueOption" && value !== "custom" && { customDueDate: "" }),
    }));

    if (touched[name]) {
      const error = validateField(name, newValue);
      setErrors((prev) => {
        const newErrors = { ...prev };
        if (error) newErrors[name] = error;
        else delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const error = validateField(name, value);
    setErrors((prev) => {
      const newErrors = { ...prev };
      if (error) newErrors[name] = error;
      else delete newErrors[name];
      return newErrors;
    });
  };

  const validateForm = () => {
    let newErrors = {};
    Object.keys(formData).forEach((key) => {
      const error = validateField(key, formData[key]);
      if (error) newErrors[key] = error;
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched(
      Object.keys(formData).reduce((acc, key) => ({ ...acc, [key]: true }), {}),
    );

    if (validateForm()) {
      setIsSubmitting(true);

      // Prepare payload based on form data
      const payload = {
        name: formData.payerName.trim(),
        phone: formData.payerPhone.trim(),
        address: formData.payerAddress.trim(),
        type: formData.type,
        amountToPay: parseFloat(formData.amountToPay),
        description: formData.description.trim(),
        dueOption: formData.dueOption,
        ...(formData.dueOption === "custom" && {
          customDueDate: formData.customDueDate,
        }),
        payNow: formData.payNow,
        ...(formData.payNow && {
          initialPaidAmount: parseFloat(formData.initialPaidAmount),
          initialNote: formData.initialNote.trim(),
        }),
      };

      try {
        await addManualPayment(payload);
        setShowSuccess(true);
        setFormData(initialFormState);
        setTouched({});
        setErrors({});
        setTimeout(() => {
          setShowSuccess(false);
          navigate("/invoices/lists");
        }, 2000);
      } catch (error) {
        console.error("Failed to add payment:", error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 max-w-4xl mx-auto pb-12 bg-background dark:bg-dark-background min-h-screen">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate("/payment/subscriptions")}
          className="p-2 rounded-xl hover:bg-accent dark:hover:bg-gray-800 transition-colors text-text dark:text-dark-text"
        >
          <ChevronLeft size={24} />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-text dark:text-dark-text">
            New Manual Payment
          </h1>
          <p className="text-gray-500 dark:text-dark-muted mt-1">
            Create a new invoice with due date and optional immediate payment.
          </p>
        </div>
      </div>

      {showSuccess && (
        <div className="mb-6 bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300 p-4 rounded-xl flex items-center gap-3 animate-in fade-in zoom-in duration-300 shadow-sm">
          <CheckCircle size={20} />
          <span className="font-medium">
            Payment registered successfully! Invoice has been generated.
          </span>
        </div>
      )}

      <div className="bg-card dark:bg-dark-surface rounded-3xl shadow-sm border border-gray-100 dark:border-dark-border p-8 md:p-10">
        <form onSubmit={handleSubmit} className="space-y-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-12 gap-y-8">
            {/* Left Column: Payer Info */}
            <div className="space-y-6">
              <h3 className="text-lg font-bold text-text dark:text-dark-text border-b border-gray-100 dark:border-dark-border pb-2 mb-4">
                Payer Information
              </h3>

              <div>
                <label className="block text-xs font-bold text-gray-500 dark:text-dark-muted uppercase mb-2">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <User
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-dark-muted"
                    size={18}
                  />
                  <input
                    type="text"
                    name="payerName"
                    value={formData.payerName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`w-full pl-11 pr-4 py-3 rounded-xl border bg-accent dark:bg-gray-900 text-text dark:text-dark-text outline-none focus:ring-2 transition-all ${
                      errors.payerName
                        ? "border-red-500 focus:ring-red-200"
                        : "border-gray-200 dark:border-dark-border focus:ring-primary/20 focus:border-primary"
                    }`}
                    placeholder="Enter payer name"
                  />
                  {errors.payerName && (
                    <AlertCircle
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-red-500"
                      size={18}
                    />
                  )}
                </div>
                {errors.payerName && (
                  <p className="text-red-500 text-xs mt-1 ml-1">
                    {errors.payerName}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-500 dark:text-dark-muted uppercase mb-2">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Phone
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-dark-muted"
                    size={18}
                  />
                  <input
                    type="tel"
                    name="payerPhone"
                    value={formData.payerPhone}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`w-full pl-11 pr-4 py-3 rounded-xl border bg-accent dark:bg-gray-900 text-text dark:text-dark-text outline-none focus:ring-2 transition-all ${
                      errors.payerPhone
                        ? "border-red-500 focus:ring-red-200"
                        : "border-gray-200 dark:border-dark-border focus:ring-primary/20 focus:border-primary"
                    }`}
                    placeholder="09..."
                    maxLength={10}
                  />
                  {errors.payerPhone && (
                    <AlertCircle
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-red-500"
                      size={18}
                    />
                  )}
                </div>
                {errors.payerPhone && (
                  <p className="text-red-500 text-xs mt-1 ml-1">
                    {errors.payerPhone}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-500 dark:text-dark-muted uppercase mb-2">
                  Address <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <MapPin
                    className="absolute left-4 top-4 text-gray-400 dark:text-dark-muted"
                    size={18}
                  />
                  <textarea
                    name="payerAddress"
                    value={formData.payerAddress}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    rows={3}
                    className={`w-full pl-11 pr-4 py-3 rounded-xl border bg-accent dark:bg-gray-900 text-text dark:text-dark-text outline-none focus:ring-2 transition-all resize-none ${
                      errors.payerAddress
                        ? "border-red-500 focus:ring-red-200"
                        : "border-gray-200 dark:border-dark-border focus:ring-primary/20 focus:border-primary"
                    }`}
                    placeholder="Enter billing address"
                  />
                </div>
                {errors.payerAddress && (
                  <p className="text-red-500 text-xs mt-1 ml-1">
                    {errors.payerAddress}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-500 dark:text-dark-muted uppercase mb-2">
                  Payer Type <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-4">
                  {["client", "caregiver"].map((type) => (
                    <div
                      key={type}
                      onClick={() => setFormData((prev) => ({ ...prev, type }))}
                      className={`cursor-pointer border rounded-xl py-3 px-4 flex items-center justify-center gap-2 transition-all ${
                        formData.type === type
                          ? "bg-primary/10 dark:bg-primary/20 border-primary text-primary dark:text-primary"
                          : "border-gray-200 dark:border-dark-border hover:bg-accent dark:hover:bg-gray-800"
                      }`}
                    >
                      <div
                        className={`w-4 h-4 rounded-full border flex items-center justify-center ${
                          formData.type === type
                            ? "border-primary bg-primary"
                            : "border-gray-400 dark:border-dark-muted"
                        }`}
                      >
                        {formData.type === type && (
                          <div className="w-1.5 h-1.5 bg-white dark:bg-dark-surface rounded-full" />
                        )}
                      </div>
                      <span className="font-bold text-sm capitalize">
                        {type}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column: Payment Details */}
            <div className="space-y-6">
              <h3 className="text-lg font-bold text-text dark:text-dark-text border-b border-gray-100 dark:border-dark-border pb-2 mb-4">
                Payment Details
              </h3>

              <div>
                <label className="block text-xs font-bold text-gray-500 dark:text-dark-muted uppercase mb-2">
                  Amount (ETB) <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="number"
                    name="amountToPay"
                    step="0.01"
                    min="0.01"
                    value={formData.amountToPay}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`w-full pl-11 pr-4 py-3 rounded-xl border bg-accent dark:bg-gray-900 text-text dark:text-dark-text outline-none focus:ring-2 transition-all ${
                      errors.amountToPay
                        ? "border-red-500 focus:ring-red-200"
                        : "border-gray-200 dark:border-dark-border focus:ring-primary/20 focus:border-primary"
                    }`}
                    placeholder="0.00"
                  />
                </div>
                {errors.amountToPay && (
                  <p className="text-red-500 text-xs mt-1 ml-1">
                    {errors.amountToPay}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-500 dark:text-dark-muted uppercase mb-2">
                  Due Date <span className="text-red-500">*</span>
                </label>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    {dueDateOptions.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() =>
                          setFormData((prev) => ({
                            ...prev,
                            dueOption: option.value,
                            ...(option.value !== "custom" && {
                              customDueDate: "",
                            }),
                          }))
                        }
                        className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                          formData.dueOption === option.value
                            ? "bg-primary text-white dark:bg-primary"
                            : "bg-accent dark:bg-gray-900 text-gray-600 dark:text-dark-muted hover:bg-gray-200 dark:hover:bg-gray-800"
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>

                  {formData.dueOption === "custom" && (
                    <div className="relative">
                      <Calendar
                        className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-dark-muted"
                        size={18}
                      />
                      <input
                        type="date"
                        name="customDueDate"
                        value={formData.customDueDate}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        min={new Date().toISOString().split("T")[0]}
                        className={`w-full pl-11 pr-4 py-2 rounded-xl border bg-accent dark:bg-gray-900 text-text dark:text-dark-text outline-none focus:ring-2 transition-all ${
                          errors.customDueDate
                            ? "border-red-500 focus:ring-red-200"
                            : "border-gray-200 dark:border-dark-border focus:ring-primary/20 focus:border-primary"
                        }`}
                      />
                      {errors.customDueDate && (
                        <p className="text-red-500 text-xs mt-1 ml-1">
                          {errors.customDueDate}
                        </p>
                      )}
                    </div>
                  )}

                  <div className="text-xs text-gray-500 dark:text-dark-muted flex items-center gap-1">
                    <Clock size={12} />
                    <span>
                      Due date:{" "}
                      {calculateDueDate()
                        ? new Date(calculateDueDate()).toLocaleDateString(
                            "en-US",
                            {
                              weekday: "short",
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            },
                          )
                        : "Not set"}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-500 dark:text-dark-muted uppercase mb-2">
                  Description / Note
                </label>
                <div className="relative">
                  <FileText
                    className="absolute left-4 top-4 text-gray-400 dark:text-dark-muted"
                    size={18}
                  />
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    rows={2}
                    className="w-full pl-11 pr-4 py-3 rounded-xl border border-gray-200 dark:border-dark-border bg-accent dark:bg-gray-900 text-text dark:text-dark-text outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
                    placeholder="Optional notes about this payment..."
                  />
                </div>
              </div>

              {/* Pay Now Section */}
              <div className="pt-4 border-t border-gray-100 dark:border-dark-border">
                <div className="flex items-center gap-3 mb-4">
                  <input
                    type="checkbox"
                    id="payNow"
                    name="payNow"
                    checked={formData.payNow}
                    onChange={handleChange}
                    className="w-4 h-4 rounded border-gray-300 dark:border-dark-border text-primary focus:ring-primary"
                  />
                  <label
                    htmlFor="payNow"
                    className="font-bold text-text dark:text-dark-text flex items-center gap-2"
                  >
                    <CreditCard size={16} />
                    Pay Now (Partial/Full Payment)
                  </label>
                </div>

                {formData.payNow && (
                  <div className="space-y-4 bg-accent/50 dark:bg-gray-900/50 p-4 rounded-xl">
                    <div>
                      <label className="block text-xs font-bold text-gray-500 dark:text-dark-muted uppercase mb-2">
                        Amount to Pay Now
                      </label>
                      <div className="relative">
                        <p>ETB</p>
                        <input
                          type="number"
                          name="initialPaidAmount"
                          step="0.01"
                          min="0.01"
                          max={formData.amountToPay || ""}
                          value={formData.initialPaidAmount}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className={`w-full pl-10 pr-4 py-2 rounded-xl border bg-white dark:bg-gray-800 text-text dark:text-dark-text outline-none focus:ring-2 transition-all ${
                            errors.initialPaidAmount
                              ? "border-red-500 focus:ring-red-200"
                              : "border-gray-200 dark:border-dark-border focus:ring-primary/20 focus:border-primary"
                          }`}
                          placeholder="Enter amount"
                        />
                      </div>
                      {errors.initialPaidAmount && (
                        <p className="text-red-500 text-xs mt-1 ml-1">
                          {errors.initialPaidAmount}
                        </p>
                      )}
                      {formData.amountToPay && (
                        <p className="text-xs text-gray-500 dark:text-dark-muted mt-1">
                          Total amount: $
                          {parseFloat(formData.amountToPay).toFixed(2)}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-500 dark:text-dark-muted uppercase mb-2">
                        Payment Note (Optional)
                      </label>
                      <input
                        type="text"
                        name="initialNote"
                        value={formData.initialNote}
                        onChange={handleChange}
                        className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-dark-border bg-white dark:bg-gray-800 text-text dark:text-dark-text outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                        placeholder="e.g., Advance payment, Deposit, etc."
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Summary Section */}
          <div className="bg-accent/30 dark:bg-gray-900/30 rounded-2xl p-6 border border-gray-100 dark:border-dark-border">
            <h3 className="text-lg font-bold text-text dark:text-dark-text mb-4 flex items-center gap-2">
              <CheckCircle size={20} />
              Payment Summary
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-card dark:bg-gray-800 p-4 rounded-xl">
                <p className="text-sm text-gray-500 dark:text-dark-muted mb-1">
                  Client
                </p>
                <p className="font-bold text-text dark:text-dark-text">
                  {formData.payerName || "Not specified"}
                </p>
              </div>
              <div className="bg-card dark:bg-gray-800 p-4 rounded-xl">
                <p className="text-sm text-gray-500 dark:text-dark-muted mb-1">
                  Amount
                </p>
                <p className="font-bold text-text dark:text-dark-text">
                  $
                  {formData.amountToPay
                    ? parseFloat(formData.amountToPay).toFixed(2)
                    : "0.00"}
                </p>
              </div>
              <div className="bg-card dark:bg-gray-800 p-4 rounded-xl">
                <p className="text-sm text-gray-500 dark:text-dark-muted mb-1">
                  Due Date
                </p>
                <p className="font-bold text-text dark:text-dark-text">
                  {calculateDueDate()
                    ? new Date(calculateDueDate()).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })
                    : "Not set"}
                </p>
              </div>
            </div>
            {formData.payNow && formData.initialPaidAmount && (
              <div className="mt-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-4 rounded-xl">
                <p className="text-sm text-green-700 dark:text-green-300 font-medium">
                  Initial payment of $
                  {parseFloat(formData.initialPaidAmount).toFixed(2)} will be
                  recorded.
                  {parseFloat(formData.initialPaidAmount) ===
                  parseFloat(formData.amountToPay || 0)
                    ? " (Full Payment)"
                    : " (Partial Payment)"}
                </p>
              </div>
            )}
          </div>

          <div className="pt-6 border-t border-gray-100 dark:border-dark-border flex justify-end gap-4">
            <button
              type="button"
              onClick={() => navigate("/payment/subscriptions")}
              className="px-6 py-3 rounded-xl border border-gray-200 dark:border-dark-border text-gray-600 dark:text-dark-muted font-bold hover:bg-accent dark:hover:bg-gray-800 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-8 py-3 rounded-xl bg-primary hover:bg-primary/90 text-white font-bold shadow-lg shadow-primary/20 dark:shadow-none transition-all flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                "Processing..."
              ) : (
                <>
                  <Save size={18} />{" "}
                  {formData.payNow
                    ? "Create Invoice with Payment"
                    : "Create Invoice"}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewInvoice;
